import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { X, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatoCantidad } from '@/lib/utils/formato-precio';
import { pricingRulesApi } from '../services/pricing-rules-api';
import type { ScopeCategory } from '../types/pricing-rules.types';

interface Props {
  selected: string[];
  onChange: (categories: string[]) => void;
}

export function CategoryMultiSelect({ selected, onChange }: Props) {
  const [categories, setCategories] = useState<ScopeCategory[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    pricingRulesApi
      .listCategories(true)
      .then(setCategories)
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(
    () => categories.filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [categories, search]
  );

  const toggle = (name: string) => {
    if (selected.includes(name)) {
      onChange(selected.filter((s) => s !== name));
    } else {
      onChange([...selected, name]);
    }
  };

  const totalProducts = categories
    .filter((c) => selected.includes(c.name))
    .reduce((sum, c) => sum + c.count, 0);

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-9"
          placeholder="Buscar categoría…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 rounded-md border p-2 min-h-[40px]">
          {selected.map((cat) => (
            <Badge key={cat} variant="secondary" className="gap-1">
              {cat}
              <button
                type="button"
                aria-label={'Sacar ' + cat}
                onClick={() => toggle(cat)}
                className="ml-1 rounded-full hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="rounded-md border max-h-48 overflow-y-auto">
        {loading && (
          <div className="p-3 text-center text-xs text-muted-foreground">Cargando categorías…</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="p-3 text-center text-xs text-muted-foreground">Sin resultados</div>
        )}
        {filtered.map((cat) => {
          const isSelected = selected.includes(cat.name);
          return (
            <button
              key={cat.name}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(cat.name)}
              className={cn(
                'w-full flex items-center justify-between px-3 py-2 text-sm border-b last:border-b-0 hover:bg-muted/50 transition-colors',
                isSelected && 'bg-primary/10'
              )}
            >
              <span className={cn(isSelected && 'font-medium')}>{cat.name}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {formatoCantidad(cat.count)} {cat.count === 1 ? 'producto' : 'productos'}
                </span>
                {isSelected && <span className="text-primary text-xs font-bold">✓</span>}
              </div>
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {selected.length} {selected.length === 1 ? 'categoría' : 'categorías'} ·{' '}
          {formatoCantidad(totalProducts)} {totalProducts === 1 ? 'producto' : 'productos'} en
          alcance
        </p>
      )}
    </div>
  );
}
